import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { GrainKey } from '../constants/onePagerConstants';
import type { OnePagerFilters, OnePagerRequest } from '../types/onePagerTypes';
import { fetchOnePager } from '../services/onePagerApi';
import { mapOnePagerResponse } from '../utils/onePagerResponseMapper';
import {
	fetchMobileError,
	fetchMobileStart,
	fetchMobileSuccess,
	selectMobileContent,
	selectMobileError,
	selectMobileLoading,
	selectMobilePage,
	selectMobileRows,
	selectMobileSize,
	selectMobileTotalRows,
	selectMobileViewBy,
	setMobilePage,
	setMobileViewBy,
	type MobileViewBy,
} from '../../../store/slices/onePagerMobileSlice';
import type { RootState } from '../../../store';

export interface UseOnePagerMobileParams {
	readonly filters: OnePagerFilters;
	readonly enabled?: boolean;
}

export interface UseOnePagerMobileResult {
	content: ReturnType<typeof selectMobileContent>;
	rows: ReturnType<typeof selectMobileRows>;
	loading: boolean;
	error: string | null;
	page: number;
	size: number;
	totalRows: number;
	selectedViewBy: MobileViewBy;
	onViewByChange: (view: MobileViewBy) => void;
	setPage: (page: number) => void;
	retry: () => void;
}

const VIEW_TO_GROUPING_FIELD: Record<MobileViewBy, OnePagerRequest['groupings'][number]['field']> = {
	region: 'REGION',
	market: 'MARKET',
	location: 'LOCATION',
};

const VIEW_TO_GRAIN_KEY: Record<MobileViewBy, GrainKey> = {
	region: 'rg',
	market: 'mk',
	location: 'lc',
};

export function useOnePagerMobile(params: UseOnePagerMobileParams): UseOnePagerMobileResult {
	const { filters, enabled = true } = params;
	const dispatch = useDispatch();

	const rows = useSelector(selectMobileRows);
	const content = useSelector(selectMobileContent);
	const loading = useSelector(selectMobileLoading);
	const error = useSelector(selectMobileError);
	const page = useSelector(selectMobilePage);
	const size = useSelector(selectMobileSize);
	const totalRows = useSelector(selectMobileTotalRows);
	const selectedViewBy = useSelector((state: RootState) => selectMobileViewBy(state));

	const request = useMemo<OnePagerRequest>(() => ({
		region: [...filters.region],
		market: [...filters.market],
		location: [...filters.location],
		locationType: [...filters.locationType],
		year: [...filters.year],
		period: [...filters.period],
		week: [...filters.week],
		groupings: [
			{
				field: VIEW_TO_GROUPING_FIELD[selectedViewBy],
				order: 1,
			},
		],
		page,
		size,
		...(filters.startDate ? { startDate: filters.startDate } : {}),
		...(filters.endDate ? { endDate: filters.endDate } : {}),
	}), [filters, selectedViewBy, page, size]);

	const executeFetch = useCallback(async () => {
		if (!enabled) return;
		dispatch(fetchMobileStart());
		try {
			const responseDTO = await fetchOnePager(request);
			const mapped = mapOnePagerResponse(responseDTO);
			dispatch(fetchMobileSuccess({
				content: responseDTO.content ?? [],
				rows: mapped.rows,
				grandTotalRow: mapped.grandTotalRow,
				totalRows: mapped.totalRows,
				page: mapped.page,
				size: mapped.size,
			}));
		} catch (err: any) {
			dispatch(fetchMobileError(err?.message || err?.response?.data?.message || 'Failed to load One Pager mobile data'));
		}
	}, [dispatch, enabled, request]);

	useEffect(() => {
		executeFetch();
	}, [executeFetch]);

	const onViewByChange = useCallback((view: MobileViewBy) => {
		dispatch(setMobileViewBy(view));
	}, [dispatch]);

	const setPage = useCallback((nextPage: number) => {
		dispatch(setMobilePage(nextPage));
	}, [dispatch]);

	const retry = useCallback(() => {
		executeFetch();
	}, [executeFetch]);

	return {
		content,
		rows,
		loading,
		error,
		page,
		size,
		totalRows,
		selectedViewBy,
		onViewByChange,
		setPage,
		retry,
	};
}

export const mobileViewToGrainKey = (view: MobileViewBy): GrainKey => VIEW_TO_GRAIN_KEY[view];
